import { Injectable } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import * as CoreReducer from '../reducers';
import * as RegionActions from '../actions/region.actions';
import * as NotificationActions from '../actions/notification.actions';
import * as MenuActions from '../actions/menu.actions';
import * as SettingsActions from '../actions/settings.actions';
import * as InstallationDashboardsActions from '../actions/installationDashboards.actions';
import { NotificationStats } from '../../../shared/models/notification-stats';
import { Notification } from '../../../shared/models/notification';
import { ChangeUserAccount } from '../../../settings/models/change-user-account';
import { ofType } from '@ngrx/effects';
import { GeocoderService } from '../../../core/geocoder/geocoder.service';

@Injectable({
  providedIn: 'root'
})
export class CoreFacade {
  // Regions
  public getRegions$ = this._store.select(CoreReducer.getRegions);

  // Notifications
  public getNotifications$ = this._store.select(CoreReducer.getNotifications);
  public getNotificationStats$ = this._store.select(CoreReducer.getNotificationStats);
  public areNotificationsLoading$ = this._store.select(CoreReducer.areNotificationsLoading);

  // Menu
  public getMenuState$ = this._store.select(CoreReducer.getMenuState);
  public getMenuActiveTab$ = this._store.select(CoreReducer.getMenuActiveTab);
  public isMenuHidden$ = this._store.select(CoreReducer.isMenuHidden);
  public isMenuLastClickedTabActive$ = this._store.select(CoreReducer.isMenuLastClickedTabActive);
  public isOpen$ = this._store.select(CoreReducer.isOpen);
  public toggleMenu$ = this._actionsSubj.pipe(ofType(MenuActions.toggleMenu.type));

  // Settings
  public getLanguage$ = this._store.select(CoreReducer.getLanguage);
  public getTimeZone$ = this._store.select(CoreReducer.getTimeZone);
  public getSettings$ = this._store.select(CoreReducer.getSettings);
  public getTag$ = (tagName: string) => this._store.select(CoreReducer.getTag, tagName);
  public editTagSuccess$ = this._actionsSubj.pipe(ofType(SettingsActions.editTagSuccess.type));

  // Geocoder
  public autocomplete$ = (text: string) => this._geocoderService.autocomplete(text);

  // InstallationDashboards
  public getInstallationDashboards$ = this._store.select(CoreReducer.getInstallationDashboards);
  public getInstallationDashboardsErrors$ = this._store.select(CoreReducer.getInstallationDashboardsErrors);

  constructor(
    private _store: Store<any>,
    private _actionsSubj: ActionsSubject,
    private _geocoderService: GeocoderService
  ) { }

  // Region
  public getRegions() {
    this._store.dispatch(RegionActions.getRegions());
  }

  // Notifications
  public notificationsInit() {
    this._store.dispatch(NotificationActions.notificationsInit());
  }
  public notificationsDisconnect() {
    this._store.dispatch(NotificationActions.notificationsDisconnect());
  }
  public getNotifications(page: number) {
    this._store.dispatch(NotificationActions.getNotifications({page}));
  }
  public loadMoreNotifications() {
    this._store.dispatch(NotificationActions.loadMoreNotifications());
  }
  public setNotificationsStats(stats: NotificationStats) {
    this._store.dispatch(NotificationActions.setNotificationsStats({stats}));
  }
  public updateNotificationsStatus(notificationsIds: number[]) {
    this._store.dispatch(NotificationActions.updateNotificationsStatus({notificationsIds}));
  }
  public pushNotification(notification: Notification) {
    this._store.dispatch(NotificationActions.pushNotification({notification}));
  }
  public updateNotification(notification: Notification) {
    this._store.dispatch(NotificationActions.updateNotification({notification}));
  }
  public getNotificationByIds(ids: number[]) {
    this._store.dispatch(NotificationActions.getNotificationByIds({ids}));
  }

  // Menu
  public showMenu() {
    this._store.dispatch(MenuActions.showMenu());
  }
  public hideMenu() {
    this._store.dispatch(MenuActions.hideMenu());
  }
  public toggleMenu() {
    this._store.dispatch(MenuActions.toggleMenu());
  }
  public setActiveTab(tab: any) {
    this._store.dispatch(MenuActions.setActiveTab({tab}));
  }
  public lastClickedTab(tab: any) {
    this._store.dispatch(MenuActions.lastClickedTab({tab}));
  }
  public openMobileMenu() {
    this._store.dispatch(MenuActions.openMobileMenu());
  }
  public closeMobileMenu() {
    this._store.dispatch(MenuActions.closeMobileMenu());
  }

  // Settings
  public getTimeZone() {
    this._store.dispatch(SettingsActions.getTimeZone());
  }
  public setTimeZone(timeZone: string) {
    this._store.dispatch(SettingsActions.setTimeZone({timeZone}));
  }
  public loadDefaultTimeZone() {
    this._store.dispatch(SettingsActions.loadDefaultTimeZone());
  }
  public getSettingsTags() {
    this._store.dispatch(SettingsActions.getSettingsTags());
  }
  public getTag(tagName: string) {
    this._store.dispatch(SettingsActions.getTag({tagName}));
  }
  public getTags(tagNames: string[]) {
    this._store.dispatch(SettingsActions.getTags({tagNames}));
  }
  public editTag(tagName: string, tagValue: any) {
    this._store.dispatch(SettingsActions.editTag({tagName, tagValue}));
  }
  public editTags(tagNames: string[], tagValue: any) {
    this._store.dispatch(SettingsActions.editTags({tagNames, tagValue}));
  }
  public deleteTag(tagName: string) {
    this._store.dispatch(SettingsActions.deleteTag({tagName}));
  }
  public deleteAccount() {
    this._store.dispatch(SettingsActions.deleteAccount());
  }
  public changeUserAccount(data: ChangeUserAccount) {
    this._store.dispatch(SettingsActions.changeUserAccount({data}));
  }
  public decodeFromToken() {
    this._store.dispatch(SettingsActions.decodeFromToken());
  }


  // InstallationDashboards
  public getInstallationDashboardsByUnitId(unitIds: number[]) {
    this._store.dispatch(InstallationDashboardsActions.getInstallationDashboardsByUnitId({unitIds}));
  }
  public setInstallationDashboardsErrors(error) {
    this._store.dispatch(InstallationDashboardsActions.getInstallationDashboardsByUnitIdFailure({error}));
  }
}
